import React, { createContext, useContext, useRef } from 'react';
import { useDayRender } from 'react-day-picker';
import { format, isSameMonth } from 'date-fns';
import { Calendar } from '../ui/calendar';
import { dayKey, statusColors } from './calendarUtils';

const Counts = createContext({});
const NoCaption = () => null;

const CalendarDay = ({ date, displayMonth }) => {
  const ref = useRef(null);
  const render = useDayRender(date, displayMonth, ref);
  const counts = useContext(Counts);
  const key = dayKey(date);
  const entry = counts[key];
  if (render.isHidden) return <div className="invisible" />;
  return (
    <button {...render.buttonProps} ref={ref} type="button" data-testid={`calendar-day-${key}`}
      aria-label={`${format(date, 'EEEE d MMMM yyyy')}, ${entry?.count || 0} posts`}
      className={`${render.buttonProps.className || ''} min-w-0 flex flex-col items-start justify-between gap-1 p-1.5 sm:p-2`}
      disabled={render.buttonProps.disabled || !isSameMonth(date, displayMonth)}>
      <span className="text-xs sm:text-sm">{date.getDate()}</span>
      {entry ? <>
        <span data-testid={`calendar-count-${key}`} className="text-xs font-medium leading-4">{entry.count}<span className="hidden md:inline"> {entry.count === 1 ? 'post' : 'posts'}</span></span>
        <span className="flex gap-1" aria-hidden="true">{Object.keys(entry.status_counts).slice(0, 3).map(status => (
          <span key={status} className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full" style={{ backgroundColor: statusColors[status] }} />
        ))}</span>
      </> : <span className="h-5" />}
    </button>
  );
};

export const PostCalendarGrid = ({ month, selected, onSelect, onMonthChange, counts, loading }) => (
  <Counts.Provider value={counts}>
    <div data-testid="post-calendar-grid" aria-busy={loading} className="min-w-0 border border-white/10 rounded-lg overflow-hidden">
      <Calendar mode="single" month={month} selected={selected} onSelect={date => date && onSelect(date)}
        onMonthChange={onMonthChange}
        weekStartsOn={1} showOutsideDays fixedWeeks components={{ Day: CalendarDay, Caption: NoCaption }}
        className="p-1 sm:p-3 w-full text-white"
        classNames={{
          months: 'w-full', month: 'w-full', table: 'w-full border-collapse',
          head_row: 'grid grid-cols-7', head_cell: 'text-white/40 text-[10px] sm:text-xs py-2 text-center min-w-0',
          row: 'grid grid-cols-7', cell: 'min-w-0 border border-white/5 p-0',
          day: 'h-[68px] sm:h-[88px] w-full rounded-none text-white/70 transition-colors hover:bg-white/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-400',
          day_selected: '!bg-emerald-500/15 !text-emerald-300 ring-1 ring-inset ring-emerald-500/60',
          day_today: 'font-bold underline decoration-emerald-400 underline-offset-4',
          day_outside: 'opacity-25', day_disabled: 'cursor-default',
        }} />
    </div>
  </Counts.Provider>
);